import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin } from "lucide-react";
import heroImage from "@/assets/hero-students-studying.jpg";
import { useIsMobile } from "@/hooks/use-mobile";
import { useParallax, useScaleParallax } from "@/hooks/use-parallax";

const Hero = () => {
  const isMobile = useIsMobile();
  
  // Parallax effects - disabled on mobile for performance
  const decorative1 = useParallax({ speed: 0.3, direction: "down", disabled: isMobile });
  const decorative2 = useParallax({ speed: 0.4, direction: "up", disabled: isMobile });
  const heroImageParallax = useScaleParallax({ speed: 0.0003, disabled: isMobile });
  
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 z-0" style={{
      background: 'var(--gradient-hero)'
    }} />
      
      {/* Hero content */}
      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <div className="space-y-8 animate-fade-in-adaptive will-animate">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-border">
              <MapPin className="w-4 h-4 text-secondary" />
              <span className="text-sm font-medium text-muted-foreground">
                Melbourne, Australia
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground leading-tight">
              <span className="hidden md:inline">Your journey in Australia begins with{" "}</span>
              <span className="md:hidden">Australia journey starts with{" "}</span>
              <span className="text-secondary">confidence.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl">
              <span className="hidden md:inline">Ace Anchor helps international students settle, connect, and start earning from Day One, because doesn't matter what, we've got you.</span>
              <span className="md:hidden">Settle, connect, and start earning from Day One in Melbourne.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="text-base group" asChild>
                <a href="#services">
                  Explore Our Plans
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button>
              <Button size="lg" variant="secondary" className="text-base" asChild>
                <a href="/consultation">
                  Book Your Spot
                </a>
              </Button>
            </div>
            
            <div className="flex items-center gap-6 md:gap-8 pt-4">
              <div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">500+</p>
                <p className="text-xs md:text-sm text-muted-foreground">Students Helped</p>
              </div>
              <div className="h-12 w-px bg-border" />
              <div>
                <p className="text-2xl md:text-3xl font-bold text-foreground">4.9/5</p>
                <p className="text-xs md:text-sm text-muted-foreground">Trust Rating</p>
              </div>
              {!isMobile && (
                <>
                  <div className="h-12 w-px bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-foreground">24/7</p>
                    <p className="text-sm text-muted-foreground">Support</p>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Right side - Image */}
          <div 
            className="relative h-[300px] md:h-[400px] lg:h-[600px] animate-scale-in-adaptive will-animate overflow-hidden" 
            style={{ animationDelay: '0.2s' }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-3xl blur-3xl" />
            <div ref={heroImageParallax.ref} style={heroImageParallax.style} className="h-full">
              <img 
                src={heroImage} 
                alt="Diverse group of international students socializing on university campus stairs" 
                className="relative rounded-3xl shadow-card w-full h-full object-cover" 
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative elements with parallax */}
      <div 
        ref={decorative1.ref}
        style={decorative1.style}
        className="absolute top-20 right-20 w-72 h-72 bg-secondary/10 rounded-full blur-3xl animate-pulse-adaptive pointer-events-none" 
      />
      <div 
        ref={decorative2.ref}
        style={decorative2.style}
        className="absolute bottom-20 left-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse-adaptive pointer-events-none" 
      />
    </section>;
};
export default Hero;